import { useEffect, useRef, useState } from "react";
import { ArrowRight } from "lucide-react";
import { trackEvent } from "@/utils/tracking";

const GOOGLE_REVIEWS_URL = "https://search.google.com/local/reviews?placeid=ChIJCWEKg8Pj2ogR3J77_w2wjNE";

/**
 * TrustIndex Reviews widget — visible ONLY on the homepage.
 *
 * Architecture:
 *  - The TrustIndex loader script lives in public/index.html so it loads early
 *    and reliably (dynamic injection fails on some mobile browsers / StrictMode).
 *  - On mount, we relocate the widget DOM into our styled `<section>`.
 *  - On unmount, we park it in a hidden stash so React can't destroy it.
 *  - CSS in App.css hides `.ti-widget` site-wide and only unhides it when
 *    inside `.trustindex-embed` (this component's container) → the widget
 *    can NEVER appear on /shop, /services/*, /blog, etc.
 *  - The same CSS also hides TrustIndex's floating docked bar.
 *  - A skeleton loader shows while the async widget fetches reviews from the
 *    trustindex.io CDN (usually ~1–3 seconds) so the section never looks empty.
 */
let cachedWidget = null;
let cachedStash = null;

function ensureStash() {
  if (cachedStash && document.body.contains(cachedStash)) return cachedStash;
  cachedStash = document.createElement("div");
  cachedStash.setAttribute("data-ti-stash", "true");
  cachedStash.style.display = "none";
  document.body.appendChild(cachedStash);
  return cachedStash;
}

function attachWidget(target) {
  const widget = cachedWidget || document.querySelector(".ti-widget");
  if (widget && target && widget.parentElement !== target) {
    cachedWidget = widget;
    target.appendChild(widget);
    return true;
  }
  return Boolean(widget);
}

function ReviewSkeleton() {
  return (
    <div className="animate-pulse" aria-hidden="true" data-testid="reviews-skeleton">
      <div className="flex items-center gap-4 mb-8 border-b border-white/5 pb-5">
        <div className="h-4 w-24 bg-white/5 rounded" />
        <div className="h-4 w-20 bg-white/5 rounded" />
        <div className="h-4 w-20 bg-white/5 rounded" />
        <div className="h-4 w-16 bg-white/5 rounded" />
        <div className="ml-auto h-9 w-28 bg-white/5 rounded-md" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {[0, 1, 2].map((i) => (
          <div key={i} className="rounded-lg border border-white/5 p-5 space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-white/5" />
              <div className="flex-1 space-y-2">
                <div className="h-3 w-24 bg-white/5 rounded" />
                <div className="h-3 w-16 bg-white/5 rounded" />
              </div>
            </div>
            <div className="flex gap-1">
              {[0, 1, 2, 3, 4].map((s) => <div key={s} className="w-3 h-3 rounded-sm bg-white/5" />)}
            </div>
            <div className="space-y-2">
              <div className="h-3 w-full bg-white/5 rounded" />
              <div className="h-3 w-11/12 bg-white/5 rounded" />
              <div className="h-3 w-4/5 bg-white/5 rounded" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Testimonials() {
  const containerRef = useRef(null);
  const [widgetReady, setWidgetReady] = useState(false);

  useEffect(() => {
    const target = containerRef.current;
    if (!target) return undefined;

    if (attachWidget(target)) {
      setWidgetReady(true);
      return () => {
        if (cachedWidget) ensureStash().appendChild(cachedWidget);
      };
    }

    const observer = new MutationObserver(() => {
      if (attachWidget(target)) {
        setWidgetReady(true);
        observer.disconnect();
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
    const stopTimer = setTimeout(() => observer.disconnect(), 20000);

    return () => {
      observer.disconnect();
      clearTimeout(stopTimer);
      if (cachedWidget) ensureStash().appendChild(cachedWidget);
    };
  }, []);

  return (
    <section
      id="reviews"
      data-testid="reviews-section"
      className="py-24 md:py-32 bg-[#0A111C]"
      aria-label="Customer Reviews"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-12">
          <p className="text-xs font-bold tracking-[0.2em] uppercase text-blue-400 mb-4">
            What Clients Say
          </p>
          <h2
            className="text-3xl md:text-4xl tracking-tight font-medium"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            Our Reputation Speaks for Itself
          </h2>
          <p className="text-sm text-slate-500 mt-3">5.0 rating · 89 reviews on Google</p>
        </div>

        <div className="relative min-h-[340px]">
          {!widgetReady && (
            <div className="absolute inset-0 px-2">
              <ReviewSkeleton />
            </div>
          )}
          <div
            ref={containerRef}
            data-testid="trustindex-widget"
            className="trustindex-embed"
          />
        </div>

        <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
          <a
            href={GOOGLE_REVIEWS_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="read-all-google-reviews-btn"
            onClick={() => trackEvent("google_reviews_click", { location: "testimonials" })}
            className="inline-flex items-center gap-2 bg-white text-[#050A11] hover:bg-slate-100 font-medium px-6 py-3 rounded-lg transition-all text-sm shadow-lg shadow-white/10"
          >
            Read all 89 reviews on Google
            <ArrowRight size={16} />
          </a>
          <p className="text-slate-500 text-xs sm:text-sm">
            Verified by <span className="text-slate-300">Google</span>, <span className="text-slate-300">Facebook</span> & <span className="text-slate-300">Yelp</span>
          </p>
        </div>
      </div>
    </section>
  );
}
